import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { RenameModelMessages } from "../models/messages/Messages";
import { modelAlreadyExistsChecker } from "../models/ModelAlreadyExistsChecker";
import eventEmitter from "../state/EventEmitter";
import { DialogBoxYesNoEvent } from "../types/DialogBoxEvents";
import { Button } from "./buttons/Button";
import { InputGroup } from "./inputGroup/InputGroup";
import { Label } from "./inputGroup/Label";
import { AdvancedTooltip } from "./tooltips/AdvancedTooltip";
import { TTTemplateName } from "./tooltips/ConfigurationTooltips";

export { ModelNameChanger };

type Props = {
  batchClassId: string;
  modelName: string;
  /**
   * Fires once the user has confirmed that the Model Name they want is correct. This includes a prompt for setting a
   * name that another Model in the system already uses.
   */
  changeModelName: (name: string, modelAlreadyExists: boolean) => void;
};

const ModelNameChanger = (props: Props) => {
  const [modelAlreadyExists, setModelAlreadyExists] = useState(false);
  const [modelName, setModelName] = useState(props.modelName);
  const [loading, setLoading] = useState(true);

  // when modelName prop changes, update state.
  useEffect(() => {
    if (props.modelName !== modelName) {
      setModelName(props.modelName);
    }
  }, [props.modelName]);

  // when model name state changes, trigger request
  useEffect(() => {
    let mounted = true;
    function handleModelAlreadyExists() {
      modelAlreadyExistsChecker(modelName, props.batchClassId).then(
        (modelUuid) => {
          if (mounted) {
            setModelAlreadyExists(modelUuid.length > 0);
            setLoading(false);
          }
        },
      );
    }
    if (modelName !== "") {
      setLoading(true);
      handleModelAlreadyExists();
    } else {
      setLoading(false);
    }
    return () => {
      mounted = false;
    };
  }, [modelName]);

  const confirmModelName = () => {
    // convert this into useASync hook
    const confirmPromise = new Promise((res) => {
      if (!modelAlreadyExists) {
        return res(true);
      }
      eventEmitter.publish(
        new DialogBoxYesNoEvent(
          RenameModelMessages.overwriteExistingModel(),
          (override: boolean) => {
            res(override);
          },
          { yes: "Confirm", no: "Cancel" },
          "Template Already Exists",
        ),
      );
    });
    confirmPromise.then((confirm) => {
      if (confirm) {
        props.changeModelName(modelName, modelAlreadyExists);
      }
    });
  };

  return (
    <div className={"modelNameChanger"}>
      <InputGroup button={modelName !== props.modelName}>
        <AdvancedTooltip tooltipText={TTTemplateName}>
          {(tProps) => (
            <Label {...tProps} ref={tProps.ref}>
              Template Name
            </Label>
          )}
        </AdvancedTooltip>
        <input
          type={"text"}
          placeholder={"A Template Name Is Required"}
          value={modelName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setModelName(e.target.value)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setTimeout(() => confirmModelName(), loading ? 1000 : 0);
            }
          }}
        />
        <Button
          style={{
            display: modelName === props.modelName ? "none" : "initial",
          }}
          disabled={modelName === "" || loading}
          onClick={() => {
            confirmModelName();
          }}
        >
          Confirm
        </Button>
        {loading && <span className={"spinner"}></span>}
      </InputGroup>
    </div>
  );
};
